// /admin/memory-shards — aggregate operator viewer for letter rows
// with letter_type='memory_shard'. Validates that the memory pipeline
// + broadcast pipeline are actually producing shards across the
// cohort, and surfaces fallback rates so a voice/context regression
// doesn't go unnoticed.
//
// Read-only: clicking a card does not navigate (the operator is not
// the bond's user — the user-facing surface is /memories on the
// bond's own session). The list shows the body inline so the operator
// can scan voice + content at a glance.

import { Link, createFileRoute } from '@tanstack/react-router';
import {
  type AdminMemoryShardsReport,
  getAdminMemoryShards,
} from '../lib/api';

export const Route = createFileRoute('/admin/memory-shards')({
  loader: async () => {
    const report = await getAdminMemoryShards();
    return { report };
  },
  component: MemoryShardsPage,
});

function relTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

function MemoryShardsPage() {
  const { report } = Route.useLoaderData();
  return (
    <section className="space-y-8">
      <header>
        <h2 className="font-display text-2xl text-ash-50 tracking-wide">Memory Shards</h2>
        <p className="mt-1 font-body text-ash-500 text-sm italic">
          Letters with type <code>memory_shard</code> across all bonds. The lore frames these as
          fragments of the Ember's forgotten past returning through the Bond.
        </p>
      </header>

      <Stats stats={report.stats} listLimit={report.listLimit} listShown={report.shards.length} />

      {report.topBonds.length > 0 && <TopBonds rows={report.topBonds} />}

      <ShardList shards={report.shards} />
    </section>
  );
}

function Stats({
  stats,
  listLimit,
  listShown,
}: {
  stats: AdminMemoryShardsReport['stats'];
  listLimit: number;
  listShown: number;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:gap-4">
      <StatCard label="All time" value={String(stats.total)} />
      <StatCard label="Last 7 days" value={String(stats.last7d)} />
      <StatCard
        label="Fallbacks"
        value={String(stats.fallbackTotal)}
        accent={stats.fallbackTotal > 0}
      />
      <StatCard
        label="Showing"
        value={String(listShown)}
        sub={listShown >= listLimit ? `of ${stats.total} (capped)` : 'recent'}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  accent = false,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-sm border bg-paper-900/40 p-4 ${
        accent ? 'border-accent-strong/40' : 'border-paper-800/60'
      }`}
    >
      <p className="font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em]">{label}</p>
      <p
        className={`mt-2 font-display text-2xl ${accent ? 'text-accent-strong' : 'text-ash-50'}`}
      >
        {value}
      </p>
      {sub && (
        <p className="mt-1 font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.3em]">
          {sub}
        </p>
      )}
    </div>
  );
}

function TopBonds({ rows }: { rows: AdminMemoryShardsReport['topBonds'] }) {
  return (
    <div>
      <h3 className="mb-2 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
        Top recipients
      </h3>
      <ul className="flex flex-wrap gap-2">
        {rows.map((b) => (
          <li
            key={b.bondId}
            className="rounded-sm border border-paper-800/60 bg-paper-900/30 px-3 py-2"
          >
            <span className="font-body text-ash-200 text-sm">{b.emberName}</span>
            <span className="ml-2 font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.3em]">
              {b.primaryElement}
            </span>
            <span className="ml-2 font-display text-[0.58rem] text-accent-strong uppercase tracking-[0.3em]">
              {b.shardCount} shard{b.shardCount === 1 ? '' : 's'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ShardList({ shards }: { shards: AdminMemoryShardsReport['shards'] }) {
  if (shards.length === 0) {
    return (
      <p className="rounded-sm border border-paper-800/60 bg-paper-900/30 p-4 font-body text-ash-300">
        No Memory Shards generated yet.
      </p>
    );
  }
  return (
    <div>
      <h3 className="mb-3 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
        Recent shards
      </h3>
      <ul className="space-y-3">
        {shards.map((s) => (
          <li
            key={s.id}
            className={`rounded-sm border p-4 lg:p-5 ${
              s.isFallback
                ? 'border-accent-strong/40 bg-paper-900/40'
                : 'border-paper-800/50 bg-paper-900/25'
            }`}
          >
            <header className="mb-2 flex flex-wrap items-center gap-3">
              <span className="font-display text-ash-100 text-sm">{s.bond.emberName}</span>
              <span className="font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.3em]">
                {s.bond.primaryElement}
              </span>
              {s.bond.relationshipState && (
                <span className="font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.3em]">
                  state: {s.bond.relationshipState}
                </span>
              )}
              <SourceChip source={s.source} />
              {s.isFallback && (
                <span className="font-display text-[0.58rem] text-accent-strong uppercase tracking-[0.3em]">
                  fallback
                  {s.retryCount > 0 ? ` · ${s.retryCount} retries` : ''}
                </span>
              )}
              <span className="ml-auto font-display text-[0.58rem] text-ash-400">
                {relTime(s.generatedAt)}
              </span>
            </header>
            {s.bond.userEmail && (
              <p className="mb-2 font-display text-[0.55rem] text-ash-600 uppercase tracking-[0.3em]">
                {s.bond.userEmail}
              </p>
            )}
            <p
              className={`whitespace-pre-line font-body text-[0.95rem] leading-relaxed ${
                s.isFallback ? 'text-ash-300 italic' : 'text-ash-100'
              }`}
            >
              {s.body}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SourceChip({ source }: { source: AdminMemoryShardsReport['shards'][number]['source'] }) {
  if (source.kind === 'organic') {
    return (
      <span className="font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.3em]">
        organic
      </span>
    );
  }
  return (
    <span className="font-display text-[0.58rem] text-accent uppercase tracking-[0.3em]">
      <Link
        to="/admin/beats"
        className="underline decoration-accent/40 underline-offset-[3px] transition hover:decoration-accent-strong"
      >
        from {source.beatId}
      </Link>
    </span>
  );
}
